import React from 'react';

import { Box, Button, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';

interface Props {
  // Who is typing, for screen readers, e.g. "Laila" or "Sam".
  who?: string;
  // What they are doing, e.g. "Checking who's clocked in".
  label?: string;
  onStop?: () => void;
}

// The "typing…" bubble shared by every chat.
export const ChatTyping: React.FC<Props> = ({ who, label, onStop }) => {
  const classes = useStyles();

  return (
    <Box className={classes.row} role="status" aria-label={who ? `${who} is typing` : 'Typing'}>
      <Box className={classes.bubble}>
        <span className={classes.dot} />
        <span className={classes.dot} />
        <span className={classes.dot} />
      </Box>
      {label && (
        <Typography variant="caption" color="textSecondary" className={classes.label}>
          {label}…
        </Typography>
      )}
      {onStop && (
        <Button size="small" onClick={onStop} className={classes.stop}>
          Stop
        </Button>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      alignSelf: 'flex-start',
      marginTop: 10,
      animation: '$enter 280ms cubic-bezier(.2,.8,.2,1) both',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    label: { fontStyle: 'italic' },
    bubble: {
      display: 'flex',
      gap: 4,
      padding: '13px 14px',
      borderRadius: 18,
      borderBottomLeftRadius: 6,
      backgroundColor: theme.palette.background.paper,
    },
    dot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      backgroundColor: theme.palette.text.secondary,
      animation: '$bounce 1.2s ease-in-out infinite',
      '&:nth-child(2)': { animationDelay: '0.15s' },
      '&:nth-child(3)': { animationDelay: '0.3s' },
      '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.6 },
    },
    stop: { color: theme.palette.text.secondary, minWidth: 0 },
    '@keyframes bounce': {
      '0%, 60%, 100%': { opacity: 0.35, transform: 'translateY(0)' },
      '30%': { opacity: 1, transform: 'translateY(-3px)' },
    },
    '@keyframes enter': {
      from: { opacity: 0, transform: 'translateY(8px)' },
      to: { opacity: 1, transform: 'none' },
    },
  })
);
